import { Suspense, lazy, useState, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";

import Intro from "./components/Intro/Intro";
import Education from "./component/resume/Education";
import Skills from "./component/resume/Skills";
import Experience from "./component/resume/Experience";
import Certificates from "./component/resume/Certificates";
import Projects from "./component/resume/Projects";
import ResumeHome from "./component/resume/ResumeHome";
import PageLoader from "./ui/PageLoader";
import Login from "./pages/Login";
import Register from "./pages/Register";
import UserAccount from "./component/user/UserAccount";
import ForgotPassword from "./component/forgotPassword/ForgotPassword";
import RecoverPassword from "./component/forgotPassword/RecoverPassword";
import DetailBlog from "./component/blog/DetailBlog";
import { ModalProvider } from "./context/ModalContext";
import BlogManagement from "./component/dashboard/BlogManagement";
import ResumeManagement from "./component/dashboard/ResumeManagement";
import CommentManagement from "./component/dashboard/CommentManagement";
import UserManagement from "./component/dashboard/UserManagement";
import CertificateManagement from "./component/dashboard/CertificateManagement";
import ActivityManagement from "./component/dashboard/ActivityManagement";
import TestimonialsManager from "./component/dashboard/TestimonialsManager";
import SettingsProfile from "./component/dashboard/SettingsProfile";
import SettingsPassword from "./component/dashboard/SettingsPassword";
import PagesManager from "./component/dashboard/PagesManager";
import DashboardHome from "./component/dashboard/DashboardHome";
import Header from "./ui/Header";
import Footer from "./pages/Footer";
import ContactCTA from "./component/footerData/ContactCTA";
import Cursor from "./ui/Cursor";
import ScrollToTop from "./ui/ScrollToTop";
import { useLayoutVisibility } from "./hooks/useLayoutVisibility";

// Dynamic imports
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Home = lazy(() => import("./pages/Home"));
const Features = lazy(() => import("./pages/Features"));
const Process = lazy(() => import("./pages/Process"));
const Resume = lazy(() => import("./pages/Resume"));
const Blog = lazy(() => import("./pages/Blog"));
const Contacts = lazy(() => import("./pages/Contacts"));
const PageNotFound = lazy(() => import("./pages/PageNotFound"));
const OAuthSuccess = lazy(() => import("./pages/OAuthSuccess"));
const OAuthError = lazy(() => import("./pages/OAuthError"));

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
      },
    },
  });

  const [openModal, setOpenModal] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  
  useEffect(() => {
    const hasSeenIntro = sessionStorage.getItem('hasSeenIntro');
    if (!hasSeenIntro) {
      setShowIntro(true);
    }
  }, []);

  const handleIntroFinish = () => {
    setShowIntro(false);
    sessionStorage.setItem('hasSeenIntro', 'true');
  };

  const location = useLocation();
  const { showHeader, showFooter, showContactCTA } = useLayoutVisibility(location.pathname);
  const close = () => setOpenModal(false);

  return (
    <div className="App">
      <ModalProvider>
        <AnimatePresence mode="wait">
          {showIntro && <Intro onFinish={handleIntroFinish} key="intro" />}
        </AnimatePresence>
        
        {!showIntro && (
          <>
            <Cursor />
            <ScrollToTop />
            <QueryClientProvider client={queryClient}>
              <Toaster
                position="top-right"
                gutter={12}
                containerStyle={{ margin: "8px", fontFamily: "Montserrat" }}
                toastOptions={{
                  success: {
                    duration: 3000,
                  },
                  error: {
                    duration: 5000,
                  },
                  style: {
                    fontSize: "12px",
                    maxWidth: "500px",
                    padding: "10px 24px",
                    fontWeight: "500",
                    background: "white",
                    color: "rgb(33, 21, 21)",
                  },
                }}
              />
              {showHeader && <Header key="header" />}
              <AnimatePresence mode="wait">
                <Suspense fallback={<PageLoader />}>
                  <Routes location={location} key={location.pathname}>
                    <Route path="dashboard" element={<Dashboard />}>
                      <Route index element={<DashboardHome />} />
                      <Route path="manage-blogs" element={<BlogManagement />} />
                      <Route path="manage-resume" element={<ResumeManagement />} />
                      <Route path="manage-comments" element={<CommentManagement />} />
                      <Route path="manage-users" element={<UserManagement />} />
                      <Route path="manage-certificates" element={<CertificateManagement />} />
                      <Route path="manage-activity" element={<ActivityManagement />} />
                      <Route path="manage-testimonials" element={<TestimonialsManager />} />
                      <Route path="settings/profile" element={<SettingsProfile />} />
                      <Route path="settings/password" element={<SettingsPassword />} />
                      <Route path="pages" element={<PagesManager />} />
                    </Route>
                    <Route index element={<Home />} />
                    <Route path="features" element={<Features />} />
                    <Route path="process" element={<Process />} />
                    <Route path="resume" element={<Resume />}>
                      <Route index element={<ResumeHome />} />
                      <Route path="education" element={<Education />} />
                      <Route path="skills" element={<Skills />} />
                      <Route path="experience" element={<Experience />} />
                      <Route path="certificates" element={<Certificates />} />
                      <Route path="projects" element={<Projects />} />
                    </Route>
                    <Route path="blog" element={<Blog />} />
                    <Route path="blog/:blogId" element={<DetailBlog />} />

                    <Route path="contacts" element={<Contacts />} />
                    <Route path="login" element={<Login />} />
                    <Route path="register" element={<Register />} />
                    <Route path="auth/oauth/success" element={<OAuthSuccess />} />
                    <Route path="auth/oauth/error" element={<OAuthError />} />
                    <Route path="me" element={<UserAccount />} />
                    <Route path="forgot-password" element={<ForgotPassword />} />
                    <Route path="update-password" element={<RecoverPassword />} />
                    <Route path="*" element={<PageNotFound />} />
                  </Routes>
                </Suspense>
              </AnimatePresence>
              {showContactCTA && <ContactCTA />}
              {showFooter && (
                <Footer
                  setOpenModal={setOpenModal}
                  openModal={openModal}
                  onClose={close}
                />
              )}
            </QueryClientProvider>
          </>
        )}
      </ModalProvider>
    </div>
  );
}

export default App;
